import type { FunctionComponent } from 'react'
import React from 'react'
import { BusyButton } from '@ztechapps/webui.button'
import { TaskActionsProvider, useTaskActionsContext } from './Context'
import type { TaskListRowActionMenuProps } from '@ztechapps/webui.contract'
import type { Project } from '@ztechapps/common.contract'
import { useMoveProjectTasks } from '@ztechapps/project.common'
import type { FormErrors } from '@ztechapps/webui.form'
import { Field, FormModal } from '@ztechapps/webui.form'
import { ProjectSelect } from '../ProjectSelect'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { RowActionMenuButtonLink } from '@ztechapps/webui.data-tools'

const moveIcon = 'right-left'
export type MoveTaskFormValues = {
  targetProjectId: string
}

export const useMoveTask = () => {
  const { taskId } = useTaskActionsContext()

  const [moveTask, { loading, error }] = useMoveProjectTasks()
  const initialValues: MoveTaskFormValues = {
    targetProjectId: null,
  }

  const validate = (values: MoveTaskFormValues) => {
    const errors = {} as FormErrors<MoveTaskFormValues>
    if (!values.targetProjectId) {
      errors.targetProjectId = 'Please select a target project.'
    }
    return errors
  }

  const onSubmit = async ({ targetProjectId }: MoveTaskFormValues): Promise<void> => {
    const transposeTargetProjectId = (targetProjectId as unknown as Project)._id

    await moveTask({
      taskIds: [taskId],
      targetProjectId: transposeTargetProjectId,
    })
  }

  return {
    initialValues,
    onSubmit,
    error,
    loading,
    validate,
  }
}

export const MoveTaskForm = () => {
  const { projectId } = useTaskActionsContext()
  const projectSelectFilter = { _id: { $ne: projectId } }

  return (
    <Field
      label="Target Project"
      help="Select a target project to move the task to."
      component={ProjectSelect}
      name="targetProjectId"
      onChange
      filter={projectSelectFilter}
    />
  )
}

export const MoveProjectTasks = ({
  trigger,
  setLoading,
}: {
  trigger?: FunctionComponent<ModalTriggerProps>
  setLoading?: (loading: boolean) => void
}) => {
  const { onSubmit: _onSubmit, initialValues, validate } = useMoveTask()
  const { canEdit } = useTaskActionsContext()

  const onSubmit = async (values: MoveTaskFormValues) => {
    try {
      setLoading?.(true)
      await _onSubmit(values)
    } finally {
      setLoading?.(false)
    }
  }

  trigger =
    trigger ??
    (({ onClick }: ModalTriggerProps) => (
      <BusyButton
        variant="primary"
        text="Move"
        icon={moveIcon}
        title="Move the task to another prject."
        disabled={!canEdit}
        onClick={onClick}
      />
    ))

  return (
    <FormModal
      title="Move Task"
      trigger={trigger}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validate}
      saveCancelButtonsProps={{
        saveText: 'Move',
        saveBusyText: 'Moving...',
        saveIcon: moveIcon,
      }}
    >
      <MoveTaskForm />
    </FormModal>
  )
}

export const MoveProjectTaskRowActionMenu = ({ task, as, taskListContext }: TaskListRowActionMenuProps) => {
  const { disabled, setLoading } = taskListContext
  const taskId = task?._id
  const projectId = task?.project?._id

  const As = as || RowActionMenuButtonLink
  const trigger = ({ onClick }: ModalTriggerProps) => (
    <As icon={moveIcon} label="Move this task" disabled={disabled} onClick={onClick} />
  )

  return (
    <TaskActionsProvider taskId={taskId} projectId={projectId} canEdit={disabled}>
      <MoveProjectTasks trigger={trigger} setLoading={setLoading} />
    </TaskActionsProvider>
  )
}
